// ストロークのスタンプ (ダブ) 補間。純ロジック (Node テスト可能)。
//
// 入力点列 → スムージング (pull-string) → 線形補間で弧長パラメータ化 →
// 間隔 (spacing × 直径) ごとにダブを置く。余り距離は次の区間へ持ち越す。

import type { Pt } from '@/types/geometry'

export interface StrokeSample {
  x: number
  y: number
  /** 0..1 (マウスは 0.5 固定 → 筆圧なし扱い) */
  pressure: number
}

export interface Dab {
  x: number
  y: number
  /** 直径 (px) */
  size: number
  /** このダブの不透明度係数 (flow × 筆圧など) */
  alpha: number
}

export interface DabSettings {
  /** 直径 px */
  size: number
  /** 間隔: 直径に対する比率 (0.01..) */
  spacing: number
  /** 流量 0..1 */
  flow: number
  /** 筆圧でサイズを変える */
  pressureSize: boolean
  /** 筆圧で流量を変える */
  pressureFlow: boolean
  /** 最小サイズ比率 (筆圧 0 のとき) */
  minSize: number
  /** 散布: 直径に対する最大ずれ (0 で無効) */
  scatter?: number
  /** サイズのジッター 0..1 */
  sizeJitter?: number
  /** 乱数 (テスト用に差し替え可能) */
  rng?: () => number
}

/**
 * 「ひも」でカーソルを引っ張るスムージング。半径 radius の円の外に出た分だけ点が動く。
 * radius = 0 で無効。
 */
export class PullStringSmoother {
  private cur: Pt | null = null
  constructor(private radius: number) {}
  reset(): void {
    this.cur = null
  }
  /** 新しい入力に対する平滑化後の位置。動かなければ null */
  feed(p: Pt): Pt | null {
    if (this.radius <= 0 || !this.cur) {
      this.cur = { x: p.x, y: p.y }
      return { ...this.cur }
    }
    const dx = p.x - this.cur.x
    const dy = p.y - this.cur.y
    const d = Math.hypot(dx, dy)
    if (d <= this.radius) return null
    const k = (d - this.radius) / d
    this.cur = { x: this.cur.x + dx * k, y: this.cur.y + dy * k }
    return { ...this.cur }
  }
  /** ペンアップ時にカーソル位置まで追いつく */
  finish(p: Pt): Pt | null {
    if (!this.cur || this.radius <= 0) return null
    const out = { x: p.x, y: p.y }
    this.cur = out
    return { ...out }
  }
}

/** ダブ生成器。ストローク中は状態 (最後の点・余り距離) を持つ */
export class DabGenerator {
  private last: StrokeSample | null = null
  private carry = 0
  private first = true

  constructor(private settings: DabSettings) {}

  private dabFor(s: StrokeSample): Dab {
    const st = this.settings
    const p = Math.max(0, Math.min(1, s.pressure))
    const sizeK = st.pressureSize ? st.minSize + (1 - st.minSize) * p : 1
    const flowK = st.pressureFlow ? p : 1
    const rng = st.rng ?? Math.random
    const jitter = st.sizeJitter ? 1 - rng() * st.sizeJitter : 1
    let x = s.x, y = s.y
    if (st.scatter) {
      const a = rng() * Math.PI * 2, r = rng() * st.scatter * st.size
      x += Math.cos(a) * r
      y += Math.sin(a) * r
    }
    return { x, y, size: Math.max(0.5, st.size * sizeK * jitter), alpha: Math.max(0, Math.min(1, st.flow * flowK)) }
  }

  /** 新しいサンプルを追加し、置くべきダブを返す */
  feed(s: StrokeSample): Dab[] {
    const out: Dab[] = []
    if (!this.last) {
      this.last = s
      if (this.first) {
        out.push(this.dabFor(s))
        this.first = false
      }
      this.carry = 0
      return out
    }
    const a = this.last
    const dx = s.x - a.x
    const dy = s.y - a.y
    const dist = Math.hypot(dx, dy)
    if (dist <= 0) return out
    let t = this.carry
    while (t <= dist) {
      const k = t / dist
      const sample: StrokeSample = { x: a.x + dx * k, y: a.y + dy * k, pressure: a.pressure + (s.pressure - a.pressure) * k }
      const dab = this.dabFor(sample)
      if (t > 0 || this.carry > 0) out.push(dab)
      const step = Math.max(0.5, dab.size * this.settings.spacing)
      t += step
    }
    this.carry = t - dist
    this.last = s
    return out
  }
}

/** ダブ列の境界矩形 (整数、余白付き) */
export function dabsBounds(dabs: Dab[]): { x: number; y: number; w: number; h: number } | null {
  if (!dabs.length) return null
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
  for (const d of dabs) {
    const r = d.size / 2 + 1
    if (d.x - r < x0) x0 = d.x - r
    if (d.y - r < y0) y0 = d.y - r
    if (d.x + r > x1) x1 = d.x + r
    if (d.y + r > y1) y1 = d.y + r
  }
  const fx = Math.floor(x0), fy = Math.floor(y0)
  return { x: fx, y: fy, w: Math.ceil(x1) - fx, h: Math.ceil(y1) - fy }
}
